import React from 'react';
import { cn } from '../../utils/cn';
import { Spinner } from './Spinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  /** Shows a leading spinner and disables the button while true. */
  isLoading?: boolean;
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  className,
  children,
  disabled,
  onClick,
  ...props
}) => {
  const isDisabled = disabled || isLoading;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    if (isDisabled) return;
    onClick?.(event);
  };
  const base = 'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-all duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)] disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none';

  const variants: Record<string, string> = {
    primary:
      'bg-[var(--primary-500)] text-white border border-[var(--primary-500)] hover:brightness-90 hover:shadow-md active:shadow-sm',
    secondary:
      'bg-[var(--muted)] text-[var(--foreground)] border border-[var(--border)] hover:brightness-95',
    outline:
      'bg-transparent text-[var(--foreground)] border border-[var(--border)] hover:bg-[var(--muted)]',
    ghost:
      'bg-transparent text-[var(--foreground)] border border-transparent hover:bg-[var(--muted)]',
    destructive:
      'bg-[var(--destructive)] text-white border border-[var(--destructive)] hover:brightness-90 hover:shadow-md',
  };

  const sizes: Record<string, string> = {
    sm: 'px-3 py-1.5 text-[13px]',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-2.5 text-base',
  };

  return (
    <button
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      onClick={handleClick}
      {...props}
    >
      {isLoading && <Spinner size="sm" />}
      {children}
    </button>
  );
};

export default Button;
